import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../App";
import { enablePush } from "../utils/useFcmPush";
import { subscribeToPush } from "../utils/push";

const POLL_MS = 30000;
const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4000/api";

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60)    return "just now";
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(dateStr).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

function getToken() {
  try { return JSON.parse(localStorage.getItem("user"))?.token || localStorage.getItem("token") || ""; }
  catch { return ""; }
}

export default function NotificationBell() {
  const { user } = useAuth();
  const [open, setOpen]     = useState(false);
  const [notes, setNotes]   = useState([]);
  const [unread, setUnread] = useState(0);
  const [permState, setPermState] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported"
  );
  const [enabling, setEnabling] = useState(false);
  const panelRef = useRef(null);
  const timerRef = useRef(null);

  const handleEnableNotifications = async () => {
    setEnabling(true);
    try {
      await Promise.all([subscribeToPush(), enablePush(user)]);
    } finally {
      setPermState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");
      setEnabling(false);
    }
  };

  const fetchNotifications = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      const res = await axios.get(`${API_BASE}/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotes(res.data?.notifications || []);
      setUnread(res.data?.count || 0);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    fetchNotifications();
    timerRef.current = setInterval(fetchNotifications, POLL_MS);
    return () => clearInterval(timerRef.current);
  }, [fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const markAllRead = async () => {
    const token = getToken();
    try {
      await axios.put(`${API_BASE}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotes(n => n.map(x => ({ ...x, is_read: true })));
      setUnread(0);
    } catch { /* silent */ }
  };

  const markOneRead = async (note) => {
    if (note.is_read || !note.dbId) return;
    const token = getToken();
    try {
      await axios.put(`${API_BASE}/notifications/${note.dbId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotes(n => n.map(x => x.id === note.id ? { ...x, is_read: true } : x));
      setUnread(u => Math.max(0, u - 1));
    } catch { /* silent */ }
  };

  const accent = "#6366f1"; // Expense app purple accent

  return (
    <div ref={panelRef} style={{ position: "relative", display: "inline-flex" }}>
      {/* Bell button */}
      <button
        onClick={() => { setOpen(o => !o); if (!open) fetchNotifications(); }}
        title="Notifications"
        aria-label="Notifications"
        style={{
          position: "relative",
          background: open ? "#f1f5f9" : "transparent",
          border: "none",
          borderRadius: 8,
          width: 36,
          height: 36,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          fontSize: 18,
          color: "#6b7280",
          transition: "background 0.15s",
        }}
      >
        🔔
        {unread > 0 && (
          <span style={{
            position: "absolute",
            top: 4, right: 4,
            background: "#ef4444",
            color: "#fff",
            borderRadius: "50%",
            width: unread > 9 ? 18 : 16,
            height: 16,
            fontSize: 10,
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "2px solid #fff",
          }}>
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div style={{
          position: "absolute",
          top: "calc(100% + 8px)",
          right: 0,
          width: 340,
          maxHeight: 460,
          background: "#fff",
          borderRadius: 14,
          boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
          border: "1px solid #e2e8f0",
          zIndex: 9999,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}>
          {/* Header */}
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "14px 16px 12px",
            borderBottom: "1px solid #f1f5f9",
          }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: "#1e293b" }}>
              🔔 Notifications
              {unread > 0 && (
                <span style={{
                  marginLeft: 8,
                  background: "#ef4444",
                  color: "#fff",
                  borderRadius: 12,
                  padding: "1px 7px",
                  fontSize: 11,
                  fontWeight: 700,
                }}>
                  {unread} new
                </span>
              )}
            </div>
            {unread > 0 && (
              <button onClick={markAllRead} style={{
                background: "none", border: "none",
                color: accent, fontSize: 12,
                cursor: "pointer", fontWeight: 600, padding: "2px 4px",
              }}>
                Mark all read
              </button>
            )}
          </div>

          {/* Enable-notifications banner — same control on desktop and mobile */}
          {permState === "default" && (
            <div style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "10px 16px", background: "#fff7ed",
              borderBottom: "1px solid #fed7aa",
            }}>
              <div style={{ flex: 1, fontSize: 12, color: "#9a3412", lineHeight: 1.3 }}>
                Get instant alerts even when the app is closed.
              </div>
              <button
                onClick={handleEnableNotifications}
                disabled={enabling}
                style={{
                  background: accent, color: "#fff", border: "none",
                  borderRadius: 8, padding: "6px 10px", fontSize: 12,
                  fontWeight: 700, cursor: enabling ? "default" : "pointer",
                  opacity: enabling ? 0.7 : 1, whiteSpace: "nowrap",
                }}
              >
                {enabling ? "Enabling…" : "🔔 Enable"}
              </button>
            </div>
          )}
          {permState === "denied" && (
            <div style={{
              padding: "8px 16px", background: "#fef2f2",
              borderBottom: "1px solid #fecaca",
              fontSize: 11, color: "#991b1b", lineHeight: 1.3,
            }}>
              Notifications are blocked in your browser settings — enable them there to get alerts.
            </div>
          )}

          {/* List */}
          <div style={{ overflowY: "auto", flex: 1 }}>
            {notes.length === 0 ? (
              <div style={{ padding: "36px 20px", textAlign: "center", color: "#94a3b8", fontSize: 14 }}>
                <div style={{ fontSize: 32, marginBottom: 10 }}>🎉</div>
                All caught up! No notifications.
              </div>
            ) : (
              notes.map(note => (
                <div
                  key={note.id}
                  onClick={() => markOneRead(note)}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    padding: "12px 16px",
                    cursor: note.dbId && !note.is_read ? "pointer" : "default",
                    background: note.is_read ? "#fff" : "#f8faff",
                    borderBottom: "1px solid #f1f5f9",
                  }}
                >
                  <div style={{
                    width: 36, height: 36, borderRadius: "50%",
                    background: note.is_read ? "#f1f5f9" : "#ede9fe",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 16, flexShrink: 0,
                  }}>
                    {note.icon || "💰"}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 13, fontWeight: note.is_read ? 500 : 700,
                      color: "#1e293b", lineHeight: 1.35, marginBottom: 2,
                    }}>
                      {note.title}
                    </div>
                    {note.subtitle && (
                      <div style={{
                        fontSize: 12, color: "#64748b", lineHeight: 1.35,
                        marginBottom: 3, overflow: "hidden",
                        textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}>
                        {note.subtitle}
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: "#94a3b8" }}>
                      {timeAgo(note.created_at)}
                    </div>
                  </div>
                  {!note.is_read && (
                    <div style={{
                      width: 8, height: 8, borderRadius: "50%",
                      background: accent, flexShrink: 0, marginTop: 4,
                    }} />
                  )}
                </div>
              ))
            )}
          </div>

        </div>
      )}
    </div>
  );
}
